<template>
  <div class="path-length-property-section" data-testid="path-length-property-section">
    <section class="panel-section">
      <div class="panel-block__header">
        <span class="panel-block__title">所选实体</span>
        <span class="panel-block__meta">{{ selectedItems.length }} 项</span>
      </div>

      <PuppyInputSelect
        v-model:active="selectionActive"
        :items="selectedItems"
        item-key-field="runtimeId"
        item-label-field="label"
        empty-text="请依次选择端到端相连的实体（线段、圆弧、样条）"
        delete-label="取消选择"
        delete-all-label="全部取消"
        @delete-item="handleDeleteItem"
        @delete-all="handleDeleteAll"
      />
    </section>

    <section v-if="totalText" class="panel-section">
      <div class="panel-block__header">
        <span class="panel-block__title">当前总长</span>
        <span class="panel-block__meta">{{ totalText }}</span>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * 路径长度命令属性面板（SW 同款：所选实体集合，✓/✗ 由 PropertyManager
 * 壳的 actions 渲染；实体列表复用 PuppyInputSelect——与"所选轮廓/所选对象"
 * 输入框同一交互契约：右键/Delete 删除单项与全部）。
 *
 * 设计说明：自拉数据模式——直接订阅智能尺寸命令快照（viewer
 * `observeSmartDimensionSnapshot`），宿主 context 不扩散命令状态字段；
 * 移除动作直接调用命令控制器公有 API。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { convertMToMm } from '@puppycad/cad-core';
import type { SmartDimensionCommandSnapshot, Viewer } from '@puppycad/cad-core';
import type { PropertyManagerContext } from '@/core';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';

const props = defineProps<{
  context: PropertyManagerContext;
}>();

/** 命令快照（订阅驱动）。 */
const snapshot = ref<SmartDimensionCommandSnapshot | null>(null);
/** 选择框激活态（PuppyInputSelect 受控）。 */
const selectionActive = ref(true);

/** 读取 viewer 句柄。 */
function getViewer(): Viewer | null {
  return (props.context.renderer as Viewer | null) ?? null;
}

/** 快照订阅（viewer 变化时重挂）。 */
let disposeSnapshotSubscription: (() => void) | null = null;

watch(
  () => props.context.renderer,
  () => {
    disposeSnapshotSubscription?.();
    snapshot.value = getViewer()?.getSmartDimensionSnapshot?.() ?? null;
    disposeSnapshotSubscription = getViewer()?.observeSmartDimensionSnapshot?.((next) => {
      snapshot.value = next;
    }) ?? null;
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  disposeSnapshotSubscription?.();
  disposeSnapshotSubscription = null;
});

/** 选择项种类 → 显示名。 */
const KIND_LABELS: Record<string, string> = {
  line: '直线',
  arc: '圆弧',
  spline: '样条',
};

/** 所选实体列表（种类名 + 序号）。 */
const selectedItems = computed(() => {
  const picks = snapshot.value?.picks ?? [];
  const counters = new Map<string, number>();

  return picks.map((pick) => {
    const kindLabel = KIND_LABELS[pick.itemKind] ?? pick.itemKind;
    const next = (counters.get(pick.itemKind) ?? 0) + 1;

    counters.set(pick.itemKind, next);

    return { runtimeId: pick.runtimeId, label: `${kindLabel} ${next}` };
  });
});

/** 当前总长文本（链合法时才有候选）。 */
const totalText = computed(() => {
  const candidate = snapshot.value?.candidate;

  if (candidate?.dimensionKind !== 'path-length') {
    return '';
  }

  return `${convertMToMm(candidate.currentValue).toFixed(2)}mm`;
});

/**
 * 移除单个已选实体。
 *
 * @param payload 删除载荷。
 */
function handleDeleteItem(payload: { item: unknown; index: number }): void {
  const runtimeId = (payload.item as { runtimeId?: string } | null)?.runtimeId;

  if (runtimeId) {
    getViewer()?.sketchSmartDimensionController?.removePickByRuntimeId(runtimeId);
  }
}

/** 移除全部已选实体。 */
function handleDeleteAll(): void {
  const controller = getViewer()?.sketchSmartDimensionController ?? null;

  selectedItems.value.forEach((item) => {
    controller?.removePickByRuntimeId(item.runtimeId);
  });
}
</script>

<style scoped>
.path-length-property-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 0 14px;
  border-bottom: 1px solid var(--n-border-color, #e5e7eb);
}

.panel-section:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.panel-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.panel-block__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--n-text-color, #1f2937);
}

.panel-block__meta {
  font-size: 12px;
  color: var(--n-text-color-3, #9ca3af);
}
</style>
